import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import { useLocalizedName, useLocalizedDescription } from '../../lib/utils/formatters';
import { fetchFeaturedMenuItems } from '../../lib/supabase/api';
import type { MenuItemWithCategory } from '../../lib/supabase/types';
import CardSwap, { Card } from '../../components/ui/CardSwap';
import { generateAltText, optimizePexelsUrl } from '../../lib/utils/imageOptimization';
import SectionTitle from '../../components/ui/SectionTitle';
import GradientBorderButton from '../../components/ui/GradientBorderButton';

const MenuPreview = () => {
  const { t } = useLanguage();
  const getLocalizedName = useLocalizedName();
  const getLocalizedDescription = useLocalizedDescription();
  const [featuredItems, setFeaturedItems] = useState<MenuItemWithCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadFeaturedItems = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const items = await fetchFeaturedMenuItems();
        setFeaturedItems(items);
      } catch (err) {
        console.error('Failed to load featured menu items:', err);
        setError(err instanceof Error ? err.message : 'Failed to load menu items');
      } finally {
        setIsLoading(false);
      }
    };

    loadFeaturedItems();
  }, []);

  const defaultImages = [
    'https://images.pexels.com/photos/8108080/pexels-photo-8108080.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    'https://images.pexels.com/photos/5639411/pexels-photo-5639411.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    'https://images.pexels.com/photos/8992935/pexels-photo-8992935.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    'https://images.pexels.com/photos/1566837/pexels-photo-1566837.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  ];

  if (isLoading) {
    return (
      <section id="menu-preview" className="py-20 bg-neutral-50">
        <div className="container-custom">
          <SectionTitle title={t.home.menuPreview.title} subtitle={t.home.menuPreview.subtitle} />
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-red-800" />
            <span className="ml-2 text-neutral-600">Loading menu items...</span>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="menu-preview" className="py-20 bg-neutral-50">
        <div className="container-custom">
          <SectionTitle title={t.home.menuPreview.title} subtitle={t.home.menuPreview.subtitle} />
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
              <p className="text-neutral-600 mb-4">Failed to load menu items</p>
              <p className="text-sm text-neutral-500">{error}</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="menu-preview" className="py-20 bg-neutral-50">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <SectionTitle
              title={t.home.menuPreview.title}
              subtitle={t.home.menuPreview.subtitle}
              align="left"
            />

            <div className="pt-4">
              <GradientBorderButton
                onClick={() => {
                  window.location.href = '/menu';
                  window.scrollTo(0, 0);
                }}
              >
                {t.home.menuPreview.button}
              </GradientBorderButton>
            </div>
          </motion.div>

          {/* Right Column - CardSwap Component */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:flex h-[600px] items-center justify-center"
          >
            <CardSwap
              cardDistance={60}
              verticalDistance={70}
              delay={5000}
              pauseOnHover={false}
              width={360}
              height={420}
            >
              {featuredItems.map((item, index) => (
                <Card key={item.id}>
                  <div
                    className={`bg-gradient-to-br ${getGradientForIndex(index)} p-6 h-full flex flex-col justify-between border border-white/10`}
                  >
                    <div>
                      <div className="w-full h-32 bg-black/20 rounded-lg mb-4 flex items-center justify-center">
                        <img
                          src={optimizePexelsUrl(
                            item.image_url || defaultImages[index % defaultImages.length],
                            400,
                            300
                          )}
                          alt={generateAltText('food', getLocalizedName(item))}
                          title={`${getLocalizedName(item)} - authentic Italian dish at Rossopomodoro Reykjavik`}
                          loading="lazy"
                          width="400"
                          height="300"
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                      <h3 className="text-white font-semibold text-lg mb-2">
                        {getLocalizedName(item)}
                      </h3>
                      <p className="text-white/80 text-sm mb-4">{getLocalizedDescription(item)}</p>
                    </div>

                    <div className="border-t border-white/20 pt-3">
                      <NavLink
                        to="/menu"
                        state={{
                          categorySlug: item.category?.name.toLowerCase().replace(/\s+/g, '-'),
                        }}
                        className="text-white/90 hover:text-white font-medium text-sm transition-colors flex items-center gap-1"
                      >
                        {t.common.viewItem}
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </NavLink>
                    </div>
                  </div>
                </Card>
              ))}
            </CardSwap>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const getGradientForIndex = (index: number): string => {
  const gradients = [
    'from-blue-600/80 to-purple-600/80',
    'from-green-600/80 to-teal-600/80',
    'from-orange-600/80 to-red-600/80',
    'from-purple-600/80 to-pink-600/80',
  ];
  return gradients[index % gradients.length];
};

export default MenuPreview;
